import React, { useEffect, useState } from "react";
import { BookOpen, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { Modal } from "./Modal";

const UI = {
  pt: { open: "Como usar esta página", back: "Voltar", next: "Próximo", done: "Entendi!", step: "Passo" },
  "pt-PT": { open: "Como utilizar esta página", back: "Voltar", next: "Seguinte", done: "Percebi!", step: "Passo" },
  en: { open: "How to use this page", back: "Back", next: "Next", done: "Got it!", step: "Step" },
  es: { open: "Cómo usar esta página", back: "Volver", next: "Siguiente", done: "¡Entendido!", step: "Paso" },
  fr: { open: "Comment utiliser cette page", back: "Retour", next: "Suivant", done: "Compris !", step: "Étape" },
  it: { open: "Come usare questa pagina", back: "Indietro", next: "Avanti", done: "Capito!", step: "Passo" },
};

// Detailed per-page walkthrough. `content[lang] = { title, steps: [{ title, text, tips?: [] }] }`.
// Opens automatically the first time (per page id) and stays available via the button.
export const PageTutorial = ({ id, content }) => {
  const { lang } = useI18n();
  const U = UI[lang] || UI.pt;
  const C = content[lang] || content.pt;
  const key = `mc_tut_${id}`;
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  useEffect(() => { if (localStorage.getItem(key) !== "done") setOpen(true); }, [key]);
  const close = () => { setOpen(false); localStorage.setItem(key, "done"); };
  const total = C.steps.length;
  const cur = C.steps[step];
  const last = step === total - 1;
  return (
    <>
      <button type="button" onClick={() => { setStep(0); setOpen(true); }} data-testid={`page-tutorial-${id}-open`}
        className="mb-6 inline-flex items-center gap-2 rounded-full border border-xmas-gold/30 bg-xmas-navycard/60 px-4 py-2 text-xs font-semibold text-xmas-gold transition hover:border-xmas-gold hover:bg-xmas-gold/10">
        <BookOpen className="h-4 w-4" /> {U.open}
      </button>
      <Modal open={open} onClose={close} title={C.title} testid={`page-tutorial-${id}`}>
        <div data-testid={`page-tutorial-${id}-step-${step}`}>
          <p className="text-[11px] uppercase tracking-widest text-xmas-champagne">{U.step} {step + 1} / {total}</p>
          <h3 className="mt-1 font-display text-xl text-xmas-snow">{cur.title}</h3>
          <p className="mt-3 text-sm leading-relaxed text-xmas-slate">{cur.text}</p>
          {cur.tips?.length > 0 && (
            <ul className="mt-4 space-y-2">
              {cur.tips.map((tip, i) => (
                <li key={i} className="flex items-start gap-2 rounded-lg bg-xmas-navy/40 px-3 py-2 text-sm text-xmas-snow">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-xmas-gold" /><span>{tip}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-6 flex items-center justify-center gap-1.5">
            {C.steps.map((_, i) => (
              <button key={i} type="button" onClick={() => setStep(i)} aria-label={`${U.step} ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-xmas-gold" : "w-1.5 bg-xmas-slate/40 hover:bg-xmas-slate/70"}`} />
            ))}
          </div>
          <div className="mt-6 flex items-center justify-between">
            {step > 0
              ? <button onClick={() => setStep(step - 1)} data-testid={`page-tutorial-${id}-back`} className="inline-flex items-center gap-1 text-sm text-xmas-slate hover:text-xmas-snow"><ArrowLeft className="h-4 w-4" /> {U.back}</button>
              : <span />}
            {last
              ? <button onClick={close} data-testid={`page-tutorial-${id}-done`} className="btn-gold rounded-full px-6 py-2.5 text-sm font-bold uppercase tracking-wider">{U.done}</button>
              : <button onClick={() => setStep(step + 1)} data-testid={`page-tutorial-${id}-next`} className="btn-gold inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-bold uppercase tracking-wider">{U.next} <ArrowRight className="h-4 w-4" /></button>}
          </div>
        </div>
      </Modal>
    </>
  );
};
